import { debounce, isEqual } from "lodash";
import PropTypes from "prop-types";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router";
import { withRouter } from "react-router";
import { Container } from "reactstrap";
import { compose } from "redux";

import { UrlProvider } from "../../../../../../api/UrlProvider";
import withTranslations from "../../../../../../hocs/withTranslations";
import DesktopNavigationLink from "./DesktopNavigationLink";
import DropdownNavigation from "./DropdownNavigation";
import withUserData from "../../../../../../hocs/withUserData";
import { showModal } from "../../../../../../lib/react-redux-modal-provider";

const DesktopNavigation = ({
  items = [],
  locale,
  translate,
  forwardedRef,
  hasPermission
}) => {
  const [dropdownNavigation, setDropdownNavigation] = useState(null);
  const history = useHistory();

  useEffect(() => {
    const unlisten = history.listen(() => {
      handleLeave();
    });

    return () => {
      unlisten();
    };
  }, []);

  const isOpenDropdown = useMemo(() => {
    return dropdownNavigation !== null;
  }, [dropdownNavigation]);

  const handleLeave = useCallback(() => {
    openDropdownNavigationDebounce.cancel();
    setDropdownNavigation(null);
  }, []);

  const openDropdownNavigation = useCallback(
    props => {
      if (typeof props?.children === "undefined") {
        handleLeave();
        return;
      }

      setDropdownNavigation(props);
    },
    [handleLeave]
  );

  const openDropdownNavigationDebounce = useCallback(
    debounce(props => {
      openDropdownNavigation(props);
    }, 300),
    [openDropdownNavigation]
  );

  const handleOpenDropdownNavigation = useCallback(
    props => {
      if (isOpenDropdown) {
        openDropdownNavigation(props);
        return;
      }

      openDropdownNavigationDebounce(props);
    },
    [
      handleLeave,
      isOpenDropdown,
      openDropdownNavigation,
      openDropdownNavigationDebounce
    ]
  );

  useImperativeHandle(forwardedRef, () => {
    return {
      isOpen: isOpenDropdown,
      open: openDropdownNavigation,
      close: handleLeave
    };
  });

  return (
    <>
      <nav className={"desktop-navigation"} onMouseLeave={handleLeave}>
        <div className={"desktop-navigation__list-wrapper"}>
          <Container tag={"ul"} className={"desktop-navigation__list"}>
            {items.map(props => {
              const { label: itemLabel } = props;
              const isActive = isEqual(props, dropdownNavigation);

              const hasPermissionToEnterPage = hasPermission(
                props?.permissions
              );

              return (
                <li key={itemLabel}>
                  <DesktopNavigationLink
                    {...props}
                    onClick={
                      hasPermissionToEnterPage
                        ? props.onClick
                        : () => {
                            showModal("PremiumModal", {
                              permissions: props?.permissions
                            });
                          }
                    }
                    href={hasPermissionToEnterPage ? props.href : undefined}
                    onEnter={() => handleOpenDropdownNavigation(props)}
                    onLeave={handleLeave}
                    active={isActive}
                  />
                </li>
              );
            })}
            <li className={"desktop-navigation__item_right"}>
              <DesktopNavigationLink
                className={"desktop-navigation__link_hero"}
                label={
                  <>
                    <i className="fas fa-star" />
                    <span>{translate("Hot stocks")}</span>
                  </>
                }
                href={UrlProvider.getUrl("fe.interestingStocks", { locale })}
                onEnter={() => handleOpenDropdownNavigation()}
              />
            </li>
          </Container>
        </div>
        {!!dropdownNavigation ? (
          <>
            <DropdownNavigation {...dropdownNavigation} />
            <div
              className={"desktop-navigation__backdrop"}
              onClick={handleLeave}
              onMouseMove={handleLeave}
            />
          </>
        ) : null}
      </nav>
    </>
  );
};

DesktopNavigation.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.arrayOf(
        PropTypes.shape({
          order: PropTypes.number,
          label: PropTypes.string,
          href: PropTypes.string
        })
      ),
      order: PropTypes.number,
      label: PropTypes.string,
      href: PropTypes.string
    })
  )
};

DesktopNavigation.displayName = "DesktopNavigation";

const ComposedDesktopNavigation = compose(
  withTranslations,
  withRouter,
  withUserData
)(DesktopNavigation);

export default forwardRef((props, ref) => (
  <ComposedDesktopNavigation {...props} forwardedRef={ref} />
));
